/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { InjectionToken } from '@angular/core';
import { EditorContentMoved } from '@app/app/shared/editor-js/model/editor-js.model';
import { CardType, EditorContent, PlaylistCardShort, Resource, ResourceSection } from '../../../../../shared/models';
import { ItemWithUploadUrl } from '../../../../../shared/models/ItemWithUploadUrl';
import { AllContentCard, AllContentPage } from '../../../../../shared/models/all-content/all-content.model';
import { CardsStatsSummary, StatsData } from '../../../../../shared/models/analytics/analytics.model';
import { Page } from '../../../../../shared/models/page';
import { ObservableResult } from '../../../../../shared/store';
import { CardTitleUpdateRequest, ResourceSectionRequest, ResourceThumbnailUpdateRequest } from '../../../models/editor';

/** Injection token for the data service, used by angular to decide whether to inject Mock or API service at runtime. */
export const CORE_RESOURCE_DATA_SERVICE = new InjectionToken<ResourceDataService>('[resource-editor] CoreResourceDataService');

/** The service handling resource CRUD operations. */
export interface ResourceDataService {
  /**
   * Updates a card's headline
   *
   * @param cardUid the UID of the resource to update
   * @param request the card details to use when updating
   * @param languageCode
   */
  updateCardHeadline(cardUid: string, request: CardTitleUpdateRequest, languageCode?: string): ObservableResult<Resource>;

  /**
   * Marks a weblink as opened
   *
   * @param resourceUid the UID of the resource to mark as open
   */
  markWebLinkOpened(resourceUid: string): ObservableResult<void>;

  /**
   * Gets the details for a resource with a given URI.
   */
  getResourceDetails(
    isProject: boolean,
    playlistUri: string,
    resourceUri: string,
    publisherUri: string,
    packageUri: string,
    pageUri: string,
    languageCode?: string,
  ): ObservableResult<Resource>;

  /**
   * Deletes a resource with a given playlistUid, sectionUid and resourceUid.
   *
   * @param playlistUid the UID of the resource parent playlist
   * @param sectionUid the UID of the resource current section
   * @param resourceUid the UID of the resource
   */
  deleteResource(playlistUid: string, sectionUid: string, resourceUid: string): ObservableResult<void>;

  /**
   * Creates a resource section with a given resourceUid.
   *
   * @param resourceUid the UID of the resource
   */
  createSection(resourceUid: string): ObservableResult<ResourceSection>;

  createEditorBlock(cardUid: string, sectionUid: string, data: EditorContent, languageCode?: string): ObservableResult<BlockUpdateResponse>;

  updateEditorBlock(cardUid: string, sectionUid: string, data: EditorContent, languageCode?: string): ObservableResult<BlockUpdateResponse>;

  deleteEditorBlock(cardUid: string, sectionUid: string, blockUid: string): ObservableResult<void>;

  moveEditorBlock(cardUid: string, sectionUid: string, event: EditorContentMoved): ObservableResult<void>;

  /**
   * Deletes a resource section with a given sectionUid and resourceUid.
   *
   * @param resourceUid the UID of the resource
   * @param sectionUid the UID of the resource current section
   */
  deleteSection(resourceUid: string, sectionUid: string): ObservableResult<Resource>;

  /**
   * Updates a resource section with a given sectionUid, resourceUid, languageCode and requstData.
   *
   * @param sectionUid the UID of the resource current section
   * @param resourceUid the UID of the resource
   * @param languageCode the language code of current tab
   * @param data request data of update
   */
  updateSection(
    resourceUid: string,
    sectionUid: string,
    data: ResourceSectionRequest,
    languageCode?: string,
  ): ObservableResult<ResourceUpdateSectionResponse>;

  /**
   * Updates a resource time required.
   *
   * @param resourceUid the UID of the resource
   * @param timeRequired the required time
   */
  updateTimeRequired(resourceUid: string, timeRequired: number): ObservableResult<void>;

  getResourceTimeRequiredSuggestion(resourceUid: string, languageCode?: string): ObservableResult<number>;

  changeResourceThumbnail(resourceUid: string, request: ResourceThumbnailUpdateRequest): ObservableResult<ItemWithUploadUrl<Resource>>;

  removeResourceThumbnail(resourceUid: string): ObservableResult<void>;

  archiveResource(resourceUid: string): ObservableResult<void>;

  addLanguage(cardUid: string, languageCode: string): ObservableResult<void>;

  removeLanguage(cardUid: string, languageCode: string): ObservableResult<void>;

  enableLanguage(cardUid: string, languageCode: string): ObservableResult<void>;

  disableLanguage(cardUid: string, languageCode: string): ObservableResult<void>;

  getResourceStartedStatistics(cardUid: string, playlistUid: string, from: string, to: string): ObservableResult<StatsData[]>;

  getResourceCompletedStatistics(cardUid: string, playlistUid: string, from: string, to: string): ObservableResult<StatsData[]>;

  getResourceStatisticsSummary(cardUid: string, playlistUid: string): ObservableResult<CardsStatsSummary>;

  getResourceCardsByTags(page: number, size: number, tags: string[]): ObservableResult<Page<PlaylistCardShort>>;

  getFormPlayListByUid(playlistUid: string, term: string, page: number, size: number): ObservableResult<Page<AllContentPage>>;

  getFormCardsByPlayListUid(playlistUid: string, selectedPlaylistUid: string): ObservableResult<AllContentCard[]>;

  getFormsList(
    playlistUid: string,
    selectedPlaylistUid: string,
    selectedCardUid: string,
  ): ObservableResult<{ title: string; id: string }[]>;

  getFormTitlesByPlayListUid(playlistUid: string): ObservableResult<{ title: string }>;

  getFormCardTitlesByCardUid(playlistUid: string, cardUid: string): ObservableResult<{ title: string }>;

  createIndependentCard(type: CardType, languageCode?: string): ObservableResult<Resource>;
}

export type ResourceUpdateSectionResponse = ItemWithUploadUrl<ResourceSection>;

export type BlockUpdateResponse = ItemWithUploadUrl<EditorContent>;
